import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  changeMembershipRole,
  companyQuery,
  engagementProfileQuery,
  engagementsQuery,
  grantMembership,
  membersQuery,
  meQuery,
  productsQuery,
  renameEngagement,
  revokeMembership,
  setCompany,
  setEngagementPlatform,
} from '../api/jedi'
import { directorySearchableQuery, type DirectoryPerson } from '../api/directory'
import { AdminTabs } from '../shell/AdminTabs'
import { NewEngagementDialog } from '../shell/NewEngagementDialog'
import { PersonPicker } from '../shell/PersonPicker'
import {
  describePlatform,
  documentedReleases,
  PlatformFields,
  platformBody,
  platformOf,
  type PlatformChoice,
} from '../shell/PlatformFields'

export const Route = createFileRoute('/_shell/admin')({
  component: AdminGate,
})

/**
 * The administration surface (ADR-0041). URL-only, like `/runs` and `/flags`:
 * the eight ratified surfaces are the product, and this is an instrument for
 * the people who run the environment. It is meant to be built out.
 *
 * The gate below is COSMETIC and recorded as cosmetic, exactly as the flags
 * route's is. The bundle ships to every browser, so hiding this page conceals
 * nothing — every call it makes is gated at the API on the `EngagementAdmin`
 * app role, which is where the boundary lives and where it is tested.
 *
 * Creating an engagement is a DELIBERATE, VISIBLE act (ADR-0041 §5). Nothing
 * here fires from an upload or a message: a person opens the dialog, fills it
 * in and presses a button, and that is the whole design rather than an
 * implementation detail. The form lived inline on this page until the second
 * topbar image moved it into a dialog, which the switcher opens too.
 */
function AdminGate() {
  const me = useQuery(meQuery)
  if (me.isPending) return null

  if (!me.data?.engagementAdmin) {
    return (
      <section className="surface">
        <header className="surface-head">
          <h1>Administration</h1>
          <p className="surface-sub">
            This page is for the people who run this environment. Ask one of them if you need
            an engagement set up.
          </p>
        </header>
      </section>
    )
  }

  return <Admin />
}

function Admin() {
  const engagements = useQuery(engagementsQuery)

  return (
    <section className="surface surface-wide">
      <header className="surface-head">
        <h1>Administration</h1>
        <p className="surface-sub">Set up and look after the engagements in this environment.</p>
        <AdminTabs />
      </header>

      <TheCompany />

      <NewEngagement />

      <h2 className="surface-h2">Engagements</h2>
      {engagements.isPending && <p className="surface-note">Loading…</p>}
      {engagements.data?.length === 0 && (
        <p className="surface-note">No engagements yet. Create the first one above.</p>
      )}
      <ul className="admin-list">
        {engagements.data?.map((engagement) => (
          <ManageEngagement key={engagement.id} id={engagement.id} name={engagement.name} />
        ))}
      </ul>
    </section>
  )
}

/**
 * Who is delivering the work: ADR-0025's Company tier, one per environment.
 *
 * IT IS NOT THE ENGAGEMENT'S ORGANIZATION, and the two were conflated until
 * this block existed. Organization is the CLIENT's parent (Johns Hopkins, or a
 * prime such as ServiceNow or KPMG) and differs per engagement; the company is
 * us and does not. Storing it per engagement meant the answer to "what are we
 * called" was written once per engagement and correctable in as many places as
 * there were engagements, and the Team surface, wanting our name, read the
 * client's.
 *
 * ABOVE THE ENGAGEMENTS ON PURPOSE. It describes the environment rather than
 * anything in the list below it, and ADR-0025 makes the provider's identity
 * swappable so a branded instance can be stood up for a partner. That is a
 * sentence about this field.
 */
function TheCompany() {
  const queryClient = useQueryClient()
  const company = useQuery(companyQuery)
  const [name, setName] = useState('')
  const [said, setSaid] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => setCompany(name.trim()),
    onSuccess: (saved) => {
      setSaid(`This environment delivers as ${saved.name}.`)
      setName('')
      // Every surface that says whose side somebody is on reads this.
      void queryClient.invalidateQueries({ queryKey: ['company'] })
    },
    onError: (error) => setSaid(refusalOf(error)),
  })

  return (
    <div className="admin-company">
      <h2 className="surface-h2">This environment</h2>
      <div className="admin-action">
        <label>
          Delivering as
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={company.data?.name ?? ''}
          />
        </label>
        <button type="button" onClick={() => save.mutate()} disabled={!name.trim() || save.isPending}>
          Save
        </button>
      </div>
      <p className="admin-hint">
        The company doing the work, wherever a surface says whose side somebody is on.
        Not the client&rsquo;s organization, which belongs to each engagement below.
        {company.data?.changedByName && ` Last set by ${company.data.changedByName}.`}
      </p>
      {said && <p className="admin-ok">{said}</p>}
    </div>
  )
}

/**
 * This page's door to the create dialog. Somebody setting up engagements here
 * may be doing it for other people, so creating one keeps them on this page
 * and says what was made, rather than taking them into its cold start the way
 * the switcher does. The sentences are the ones the inline form used to say.
 */
function NewEngagement() {
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<string | null>(null)

  return (
    <div className="admin-new">
      <button
        type="button"
        className="dialog-primary"
        onClick={() => {
          setCreated(null)
          setCreating(true)
        }}
      >
        New engagement
      </button>
      {created && <p className="admin-ok">{created}</p>}
      <NewEngagementDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={async (response) => {
          setCreated(
            response.outcomeSetId
              ? `${response.name} is ready. The outcome you named is waiting for your approval on it.`
              : `${response.name} is ready. It has no outcome yet, and cannot hold deliverables until it does.`,
          )
          void queryClient.invalidateQueries({ queryKey: ['me'] })
          await queryClient.invalidateQueries({ queryKey: ['engagements'] })
        }}
      />
    </div>
  )
}

/**
 * A refusal in J.E.D.I.'s words, unwrapped from the transport line. Local
 * rather than shared, on purpose: `ChangeSetReview.refusalOf` always returns
 * something and `_shell.work.completionRefusalOf` returns null unless it is a
 * 409 it understands. Those are different contracts, not duplicates, and this
 * one is a third — it reads `says` from a 409 AND a plain string body from a
 * 404, because the remove path refuses both ways.
 */
function refusalOf(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error)
  const body = /^API \d+ on \S+: (.+)$/s.exec(message)?.[1] ?? message
  try {
    const parsed: unknown = JSON.parse(body)
    if (typeof parsed === 'string') return parsed
    if (parsed && typeof parsed === 'object' && 'says' in parsed) {
      const says = (parsed as { says?: unknown }).says
      if (typeof says === 'string') return says
    }
  } catch {
    // not JSON — render as carried
  }
  return body
}

/**
 * The three roles in plain words. The tokens stay out of copy (ADR-0040 §2).
 *
 * `steward` arrived with ADR-0051 and is the reason Team can be the everyday
 * home for staffing: it carries the four membership acts, scoped to the one
 * engagement it is held on. This page is where it is HANDED OUT, because a
 * steward cannot make another steward — otherwise the grant would walk across
 * an environment one engagement at a time.
 */
const ROLE_WORDS: Record<string, string> = {
  contributor: 'Can read and propose',
  approver: 'Can also decide',
  steward: 'Can also manage the team',
}

function ManageEngagement({ id, name }: { id: string; name: string }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [rename, setRename] = useState('')
  const [upn, setUpn] = useState('')
  const [role, setRole] = useState('contributor')
  const [said, setSaid] = useState<string | null>(null)

  /*
    Who is on this engagement changed, so everything that reads it refreshes:
    this list, and the administrator's own directory and session, which change
    too when the person added, removed or re-roled is themselves.
  */
  const refreshMembers = () => {
    void queryClient.invalidateQueries({ queryKey: ['eng', id, 'memberships'] })
    void queryClient.invalidateQueries({ queryKey: ['engagements'] })
    void queryClient.invalidateQueries({ queryKey: ['me'] })
  }

  const doRename = useMutation({
    mutationFn: () => renameEngagement(id, { name: rename.trim() }),
    onSuccess: (profile) => {
      setSaid(`Now called ${profile.name}.`)
      setRename('')
      void queryClient.invalidateQueries({ queryKey: ['engagements'] })
    },
  })

  /*
    THE ENGAGEMENT'S OWN PARTY TIERS, correctable at last.

    `PATCH /engagements/{id}` has accepted the organization and the customer
    since it was written, and `renameEngagement` has taken them since it was
    written, and nothing ever sent them: the page offered one field labelled
    Rename and the other two were unreachable. So an engagement created with a
    mistyped client, or a seeded one created with none at all, could not be
    corrected anywhere in the product.

    THREE DIFFERENT AXES, and keeping them apart is the point (ADR-0025):
    ORGANIZATION is the client's parent (Johns Hopkins, or a prime such as
    ServiceNow); CUSTOMER is the serviced unit inside it; PRODUCT is the
    platform the work is delivered ON. A prime can appear as an Organization
    and a platform of the same name be the Product, which is why they are two
    fields rather than one (Erik, 2026-09-25: "yes they are separate").
  */
  const profile = useQuery({ ...engagementProfileQuery(id), enabled: open })
  const [tiers, setTiers] = useState({ organizationName: '', customerName: '' })
  const doTiers = useMutation({
    mutationFn: () => renameEngagement(id, {
      organizationName: tiers.organizationName.trim() || null,
      customerName: tiers.customerName.trim() || null,
    }),
    onSuccess: (updated) => {
      setSaid(`${updated.name}: client ${updated.organizationName ?? 'not set'}, `
        + `customer ${updated.customerName ?? 'not set'}.`)
      setTiers({ organizationName: '', customerName: '' })
      // The profile's own key. This used to invalidate a key no query has, so the
      // placeholders went on showing the old names until something else refetched.
      queryClient.setQueryData(engagementProfileQuery(id).queryKey, updated)
      void queryClient.invalidateQueries({ queryKey: ['engagements'] })
    },
    onError: (error) => setSaid(refusalOf(error)),
  })
  const noTierTyped = !tiers.organizationName.trim() && !tiers.customerName.trim()

  /*
    WHAT THE ENGAGEMENT IS DELIVERED ON, and the customer's release (RFC-0013's
    2026-10-07 amendment). Pickers rather than the free-text box this used to be:
    the advisor grounds on these values, so they are chosen from what JEDI knows.
    Unlike the names above, they show what is recorded, because a picker has no
    placeholder, and they are saved whole, so emptying the upgrade clears it.
  */
  const products = useQuery({ ...productsQuery, enabled: open })
  const recordedPlatform = platformOf(profile.data ?? undefined)
  const [platformEdit, setPlatformEdit] = useState<PlatformChoice | null>(null)
  const platform = platformEdit ?? recordedPlatform
  const platformUnchanged = platform.product === recordedPlatform.product
    && platform.release === recordedPlatform.release
    && platform.upgradingTo === recordedPlatform.upgradingTo
  const doPlatform = useMutation({
    mutationFn: () => setEngagementPlatform(id, platformBody(platform)),
    onSuccess: (updated) => {
      setSaid(`${updated.name}: delivered on ${describePlatform(products.data, updated) ?? 'what you chose'}.`)
      setPlatformEdit(null)
      queryClient.setQueryData(engagementProfileQuery(id).queryKey, updated)
      void queryClient.invalidateQueries({ queryKey: ['engagements'] })
    },
    onError: (error) => setSaid(refusalOf(error)),
  })
  const documented = documentedReleases(products.data, platform.product)

  // The member list, which this surface did not render until wave 20. Adding
  // somebody to a list you cannot read is a write-only affordance, and it is
  // also the precondition for offering Remove at all.
  const members = useQuery({ ...membersQuery(id), enabled: open })

  /*
    Who is being added: picked from the directory where this environment can
    search it (ADR-0045), typed where it cannot. The typed field is the
    fallback rather than a second way in, so it only appears when the picker
    cannot, and it says why.
  */
  const directory = useQuery({ ...directorySearchableQuery, enabled: open })
  const [directoryFailed, setDirectoryFailed] = useState(false)
  const searchable = directory.data === true && !directoryFailed
  const [chosen, setChosen] = useState<DirectoryPerson | null>(null)
  // Bumped after a grant, which remounts the picker and so empties it.
  const [pickerRound, setPickerRound] = useState(0)
  const grantee = searchable ? (chosen?.userPrincipalName ?? '') : upn.trim()
  // The line beneath the add row. Whom a pick will add, or why there is no
  // picker; the second only once the answer is known, because until then the
  // picker may still be about to replace the typed field.
  const addNote = searchable
    ? (chosen ? `Adds ${chosen.userPrincipalName}` : '')
    : directory.isSuccess || directoryFailed
      ? 'Directory search is not available here, so type the exact address they sign in with.'
      : ''

  const doGrant = useMutation({
    mutationFn: () => grantMembership(id, grantee, role),
    onSuccess: (granted) => {
      // An unbound membership is visibly unbound (ADR-0040 §1) — saying so is
      // the difference between "nothing happened" and "waiting for them".
      setSaid(
        granted.hasLogin
          ? `${granted.upn} can now open ${name}.`
          : `${granted.upn} is on ${name}. It takes effect the first time they sign in with that exact address.`,
      )
      setUpn('')
      setChosen(null)
      setPickerRound((round) => round + 1)
      // `membersQuery` caches for five minutes, so without this the list below
      // would not show somebody the admin just added. The grant has always
      // needed it; nothing read the list until now, so nothing noticed.
      refreshMembers()
    },
  })

  /*
    Removal (RAID #402). Two refusals, and both are sentences rather than
    statuses: 404 when the membership is not there, and 409 when they are the
    last person who can decide here.

    Kept as its own error slot rather than folded into the pair above, because
    a refused REMOVE is the one message on this surface where reading the exact
    words matters — it names what would clear it.
  */
  const [removeSaid, setRemoveSaid] = useState<string | null>(null)
  const doRevoke = useMutation({
    mutationFn: (who: string) => revokeMembership(id, who),
    onSuccess: (revoked) => {
      setRemoveSaid(
        `${revoked.upn} can no longer open ${name}. `
        + (revoked.remainingApprovers === 1
          ? 'One person can still decide here.'
          : `${revoked.remainingApprovers} people can still decide here.`),
      )
      refreshMembers()
    },
    onError: (error) => setRemoveSaid(refusalOf(error)),
  })

  /*
    Changing what somebody may do without removing them (asked for 2026-09-17:
    the list showed each role as text, so the only way to change one was to
    remove the person and add them back). Its own call rather than a re-grant,
    because a grant from a stale list could re-admit somebody just removed.

    The select shows the role the SERVER holds, never a local copy, so a
    refusal (409: they are the last person who can decide) snaps it back to the
    truth instead of leaving it showing a change that did not happen.
  */
  const [roleSaid, setRoleSaid] = useState<string | null>(null)
  const doChangeRole = useMutation({
    mutationFn: ({ who, to }: { who: string; to: string }) => changeMembershipRole(id, who, to),
    onSuccess: (changed) => {
      // The confirmed role goes straight into the list, so the select does not
      // show the old one again while the refetch below is in flight.
      queryClient.setQueryData(membersQuery(id).queryKey, (rows) =>
        rows?.map((m) => (m.upn === changed.upn ? { ...m, role: changed.role } : m)))
      setRoleSaid(
        !changed.changed
          ? `${changed.upn} already had that role on ${name}.`
          : changed.role === 'approver'
            ? `${changed.upn} can now decide on ${name}, as well as read and propose.`
            : `${changed.upn} can read and propose on ${name}, and can no longer decide.`,
      )
      refreshMembers()
    },
    onError: (error) => setRoleSaid(refusalOf(error)),
  })
  const memberBusy = doRevoke.isPending || doChangeRole.isPending

  return (
    <li className="admin-row">
      {/*
        THE ROW HAS TO LOOK LIKE IT OPENS (block H1, found 2026-09-15 by Erik,
        who could not find where to add somebody to an engagement).

        It was already a <button>, so it was reachable and announced as one -
        and it rendered as plain text with no caret, so the page read as
        "create engagements" and hid "manage engagements". Rename, Add someone,
        the member list and Remove all sit behind this click, which makes it
        the surface a pilot administrator uses to let anybody in at all:
        `environment-provisioning.md` §10 step 3 says "admit the people who
        will use it, in-product", and that assumes they can find it.

        The chevron and its rotation follow the ratified collapse recipe
        (`design/system/guidelines/collapse-motion.pattern.md`: "chevrons
        rotate on the same duration/curve as their section"), rather than an
        affordance invented here.
      */}
      <button
        type="button"
        className="admin-row-head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="admin-row-caret" aria-hidden="true">›</span>
        <span className="admin-row-name">{name}</span>
        <span className="admin-row-id">{id}</span>
      </button>

      {open && (
        <div className="admin-row-body">
          <div className="admin-action">
            <label>
              Rename
              <input value={rename} onChange={(e) => setRename(e.target.value)} placeholder={name} />
            </label>
            <button type="button" onClick={() => doRename.mutate()} disabled={!rename.trim()}>
              Rename
            </button>
          </div>

          {/*
            Each field shows what is recorded as its PLACEHOLDER and is empty
            until somebody types, which is the rename field's own shape: a
            pre-filled box invites a reader to re-save a value nobody meant to
            touch, and only what is typed is sent.
          */}
          <div className="admin-action">
            <label>
              Client organization
              <input
                value={tiers.organizationName}
                onChange={(e) => setTiers((t) => ({ ...t, organizationName: e.target.value }))}
                placeholder={profile.data?.organizationName ?? 'Not recorded'}
              />
            </label>
            <label>
              Customer
              <input
                value={tiers.customerName}
                onChange={(e) => setTiers((t) => ({ ...t, customerName: e.target.value }))}
                placeholder={profile.data?.customerName ?? 'Not recorded'}
              />
            </label>
            <button type="button" onClick={() => doTiers.mutate()} disabled={noTierTyped}>
              Save
            </button>
          </div>
          <p className="admin-hint">
            The client&rsquo;s parent organization and the unit served inside it. Not
            who is delivering it, which is one value for this environment and sits above.
          </p>

          <div className="admin-action">
            <PlatformFields value={platform} onChange={setPlatformEdit} />
            <button
              type="button"
              onClick={() => doPlatform.mutate()}
              disabled={platformUnchanged || !profile.data || doPlatform.isPending}
            >
              Save
            </button>
          </div>
          {documented && <p className="admin-hint">{documented}</p>}

          {/*
            The field, the role and Add are ONE row, level with each other, and
            what is said about the field goes on its own line beneath the whole
            row. That line sat inside the field's column until 2026-09-18, so
            picking somebody made the column taller than its neighbours and the
            bottom-aligned role and Add dropped to the line instead of the field.
            Found by Erik on dev; the harness measures it now.
          */}
          <div className="admin-add">
            <div className="admin-action">
              {searchable ? (
                <PersonPicker
                  key={pickerRound}
                  label="Add someone"
                  // This picker GRANTS MEMBERSHIP, so a second grant to the
                  // same person is the thing to refuse.
                  unavailable={(members.data ?? []).map((member) => member.upn)}
                  unavailableNote="Already on this engagement"
                  onChoose={setChosen}
                  onUnavailable={(typed) => {
                    setUpn(typed)
                    setDirectoryFailed(true)
                  }}
                />
              ) : (
                <label>
                  Add someone
                  <input
                    value={upn}
                    onChange={(e) => setUpn(e.target.value)}
                    placeholder="name@company.com"
                    aria-describedby={`${id}-add-note`}
                  />
                </label>
              )}
              <select value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="contributor">Can read and propose</option>
                <option value="approver">Can also decide</option>
              </select>
              <button type="button" onClick={() => doGrant.mutate()} disabled={!grantee}>
                Add
              </button>
            </div>
            {/* Always in the page, so a screen reader hears the pick as it lands. */}
            <p id={`${id}-add-note`} className="admin-hint" aria-live="polite">
              {addNote}
            </p>
          </div>

          {/*
            Who is here. `hasLogin` is shown rather than hidden, because an
            unbound membership is one nobody has ever signed in as and the
            record's own summary says a picker "can honestly show who has a
            login". It is also the difference between "they cannot get in" and
            "they have not tried yet".
          */}
          <ul className="admin-members">
            {members.isPending && <li className="admin-member-note">Loading…</li>}
            {members.data?.map((member) => (
              <li key={member.upn} className="admin-member">
                <span className="admin-member-who">{member.upn}</span>
                <select
                  className="admin-member-role"
                  aria-label={`What ${member.upn} can do`}
                  // The choice in flight while it is in flight, then the
                  // server's answer. React snaps a controlled select back to its
                  // value after the change event, so without this it would show
                  // the OLD role for the whole round trip.
                  value={
                    doChangeRole.isPending && doChangeRole.variables?.who === member.upn
                      ? doChangeRole.variables.to
                      : member.role
                  }
                  disabled={memberBusy}
                  onChange={(e) => doChangeRole.mutate({ who: member.upn, to: e.target.value })}
                >
                  {Object.entries(ROLE_WORDS).map(([value, words]) => (
                    <option key={value} value={value}>{words}</option>
                  ))}
                </select>
                {/* Unbound: nobody has signed in with this exact address. On
                    test that was a person who HAD signed in, under an address
                    one letter different, so the hint names that case too. */}
                {!member.hasLogin && (
                  <span
                    className="admin-member-pending"
                    title="If they have signed in, this is not the address they use."
                  >
                    Has not signed in yet
                  </span>
                )}
                <button
                  type="button"
                  className="admin-member-remove"
                  disabled={memberBusy}
                  onClick={() => doRevoke.mutate(member.upn)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>

          {(doRename.isError || doGrant.isError) && (
            <p className="admin-error">
              {((doRename.error ?? doGrant.error) as Error).message}
            </p>
          )}
          {said && <p className="admin-ok">{said}</p>}
          {removeSaid && (
            <p className={doRevoke.isError ? 'admin-error' : 'admin-ok'}>{removeSaid}</p>
          )}
          {roleSaid && (
            <p className={doChangeRole.isError ? 'admin-error' : 'admin-ok'}>{roleSaid}</p>
          )}
        </div>
      )}
    </li>
  )
}
